import { useMemo, useState, useEffect } from 'react';
import {
  Box, Stack, Typography, Chip, Card, CardContent, Collapse,
  FormControl, Select, MenuItem, CircularProgress, Alert, alpha, useTheme,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { STANDARDS, scoreStandard } from './complianceMappings';

const OBLIGATION_OPTS = [
  { value: 'unset', label: 'Not assessed' },
  { value: 'met', label: 'Met' },
  { value: 'not_met', label: 'Not met' },
  { value: 'na', label: 'N/A' },
];

const pctColor = (pct) => pct == null ? 'default' : pct >= 90 ? 'success' : pct >= 70 ? 'warning' : 'error';

/**
 * Compliance standards-profile + per-standard scorecard. Reads which standards
 * apply and the manual obligation statuses from the `complianceprofile` record;
 * computes a single compliance number per applied standard against the live
 * health-check evaluations + the doc-derived complianceMappings.
 *
 * Props:
 *   code, checks (CHECKS), evals (checkKey→evaluation), profile (complianceprofile doc),
 *   jApi, onSaved (reload), onOpenCheck (check → open the raw-data drawer)
 */
export default function ComplianceSection({ code, checks, evals, profile, jApi, onSaved, onOpenCheck }) {
  const theme = useTheme();
  const checksByKey = useMemo(() => {
    const m = {}; for (const c of (checks || [])) m[c.key] = c; return m;
  }, [checks]);

  // Working state derived from the stored profile.
  const [appliesBy, setAppliesBy] = useState({});
  const [oblBy, setOblBy] = useState({});           // { standard: { obligationKey: status } }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const a = {}, o = {};
    const stored = (profile && Array.isArray(profile.standards)) ? profile.standards : [];
    for (const s of stored) {
      a[s.name] = !!s.applies;
      o[s.name] = {};
      for (const ob of (s.obligations || [])) {
        if (ob && ob.key && ob.status) o[s.name][ob.key] = ob.status;
      }
    }
    setAppliesBy(a);
    setOblBy(o);
  }, [profile]);

  // Persist the full standards array to the complianceprofile record.
  const persist = async (nextApplies, nextObl) => {
    try {
      setSaving(true);
      setError(null);
      const standards = STANDARDS.map(name => ({
        name,
        applies: !!nextApplies[name],
        obligations: Object.entries(nextObl[name] || {})
          .filter(([, status]) => status && status !== 'unset')
          .map(([key, status]) => ({ key, status })),
      }));
      await jApi.request('PATCH', 'cma/complianceprofile', { account: code, standards });
      if (onSaved) onSaved();
    } catch (ex) {
      setError(ex?.response?.data?.error?.message || ex?.toString?.() || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Single-select: applying a standard clears any previously applied one.
  // Clicking the already-applied standard turns it off (no standard applied).
  const toggleApplies = (name) => {
    const wasOn = !!appliesBy[name];
    const next = {};
    for (const s of STANDARDS) next[s] = false;
    if (!wasOn) next[name] = true;
    setAppliesBy(next);
    persist(next, oblBy);
  };

  const setObligation = (name, key, status) => {
    const nextStd = { ...(oblBy[name] || {}), [key]: status };
    const next = { ...oblBy, [name]: nextStd };
    setOblBy(next);
    persist(appliesBy, next);
  };

  const appliedStandards = STANDARDS.filter(s => appliesBy[s]);

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">Standards profile</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Select the standard that applies to this customer (one at a time — choosing a new standard clears the
        previous). The applied standard earns a single compliance number from the automated checks plus its
        manual obligations.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {STANDARDS.map(name => (
          <Chip key={name} label={name}
            color={appliesBy[name] ? 'primary' : 'default'}
            variant={appliesBy[name] ? 'filled' : 'outlined'}
            onClick={() => toggleApplies(name)} disabled={saving} />
        ))}
        {saving && <CircularProgress size={18} sx={{ alignSelf: 'center' }} />}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {appliedStandards.length === 0 && (
        <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
          No standards applied yet. Toggle a standard above to see its scorecard.
        </Typography>
      )}

      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {appliedStandards.map(name => (
          <StandardCard key={name} name={name} checks={checks} evals={evals}
            obligationStatus={oblBy[name] || {}} checksByKey={checksByKey}
            onSetObligation={(key, status) => setObligation(name, key, status)}
            onOpenCheck={onOpenCheck} saving={saving} theme={theme} />
        ))}
      </Stack>
    </Box>
  );
}

function StandardCard({ name, checks, evals, obligationStatus, checksByKey, onSetObligation, onOpenCheck, saving, theme }) {
  const [showObl, setShowObl] = useState(false);
  const [showGaps, setShowGaps] = useState(true);

  const result = useMemo(
    () => scoreStandard(name, checks, evals, obligationStatus),
    [name, checks, evals, obligationStatus]
  );

  const gaps = result.mapped.filter(m => m.state === 'gap' || m.state === 'partial');
  const mappedPass = result.mapped.filter(m => m.state === 'pass').length;
  const mappedScored = result.mapped.filter(m => m.state !== 'nosignal').length;
  const oblMet = result.obligations.filter(o => o.status === 'met').length;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {result.lineItems} in-scope line item{result.lineItems === 1 ? '' : 's'}
              {result.outOfScope > 0 ? ` · ${result.outOfScope} out of scope` : ''}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Chip label={result.pct == null ? 'No data' : `${result.pct}%`} color={pctColor(result.pct)}
              sx={{ fontWeight: 800, fontSize: 18, height: 36, px: 1 }} />
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={`Automated: ${mappedPass}/${mappedScored} passing`} />
          <Chip size="small" variant="outlined" label={`Obligations: ${oblMet}/${result.obligations.length} met`} />
        </Stack>

        {/* Mapped-check gaps */}
        {gaps.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => setShowGaps(v => !v)}>
              {showGaps ? <KeyboardArrowDownRoundedIcon fontSize="small" /> : <KeyboardArrowRightRoundedIcon fontSize="small" />}
              <Typography variant="overline" color="text.secondary">Failing / partial checks ({gaps.length})</Typography>
            </Stack>
            <Collapse in={showGaps}>
              <Stack spacing={0.75} sx={{ pl: 1, mt: 0.5 }}>
                {gaps.map(g => (
                  <Box key={g.ref}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip size="small" color={g.state === 'gap' ? 'error' : 'warning'} variant="outlined"
                        label={g.state === 'gap' ? 'gap' : 'partial'} sx={{ height: 18 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{g.ref}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{g.label}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ pl: 3, mt: 0.25 }}>
                      {g.failingKeys.map(k => (
                        <Chip key={k} size="small" variant="outlined" label={checksByKey[k]?.label || k}
                          onClick={onOpenCheck && checksByKey[k] ? () => onOpenCheck(checksByKey[k]) : undefined}
                          sx={{ height: 18, cursor: onOpenCheck ? 'pointer' : 'default' }} />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {/* Manual obligations */}
        {result.obligations.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => setShowObl(v => !v)}>
              {showObl ? <KeyboardArrowDownRoundedIcon fontSize="small" /> : <KeyboardArrowRightRoundedIcon fontSize="small" />}
              <Typography variant="overline" color="text.secondary">Manual obligations ({result.obligations.length})</Typography>
            </Stack>
            <Collapse in={showObl}>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {result.obligations.map(o => (
                  <Stack key={o.key} direction="row" alignItems="center" spacing={1}
                    sx={{ py: 0.25, borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                    <Typography variant="caption" sx={{ flex: 1, wordBreak: 'break-word', pl: 1 }}>
                      <strong>{o.ref}</strong> — {o.label}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select value={o.status || 'unset'} disabled={saving}
                        onChange={e => onSetObligation(o.key, e.target.value)}
                        sx={{ height: 30, fontSize: 13 }}>
                        {OBLIGATION_OPTS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Stack>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
